// Hook for detecting online/offline status
// Used for PWA offline mode functionality

import { useState, useEffect, useCallback } from 'react';
import { offlineStore } from '../store/offline.store';

interface OfflineStatus {
  isOnline: boolean;
  isOffline: boolean;
  lastOnlineAt: Date | null;
  lastSyncAt: Date | null;
  syncQueueCount: number;
  connectionType: string | null;
  effectiveType: string | null;
  downlink: number | null;
  rtt: number | null;
}

interface NetworkInformation extends EventTarget {
  type?: string;
  effectiveType?: string;
  downlink?: number;
  rtt?: number;
  saveData?: boolean;
  onchange?: EventListener;
}

declare global {
  interface Navigator {
    connection?: NetworkInformation;
    mozConnection?: NetworkInformation;
    webkitConnection?: NetworkInformation;
  }
}

export function useOfflineStatus(): OfflineStatus & {
  refreshStatus: () => Promise<void>;
} {
  const [isOnline, setIsOnline] = useState<boolean>(
    typeof navigator !== 'undefined' ? navigator.onLine : true
  );
  const [lastOnlineAt, setLastOnlineAt] = useState<Date | null>(
    typeof navigator !== 'undefined' && navigator.onLine ? new Date() : null
  );
  const [lastSyncAt, setLastSyncAt] = useState<Date | null>(null);
  const [syncQueueCount, setSyncQueueCount] = useState<number>(0);
  const [connectionInfo, setConnectionInfo] = useState<{
    type: string | null;
    effectiveType: string | null;
    downlink: number | null;
    rtt: number | null;
  }>({
    type: null,
    effectiveType: null,
    downlink: null,
    rtt: null,
  });

  // Get network connection info
  const updateConnectionInfo = useCallback(() => {
    const connection =
      navigator.connection ||
      navigator.mozConnection ||
      navigator.webkitConnection;

    if (connection) {
      setConnectionInfo({
        type: connection.type || null,
        effectiveType: connection.effectiveType || null,
        downlink: connection.downlink ?? null,
        rtt: connection.rtt ?? null,
      });
    }
  }, []);

  // Refresh offline data status
  const refreshStatus = useCallback(async () => {
    try {
      const stats = await offlineStore.getOfflineDataStats();
      setSyncQueueCount(stats.syncQueueCount);
      setLastSyncAt(stats.lastDevicesSync);
    } catch (error) {
      console.error('Error getting offline status:', error);
    }
  }, []);

  // Handle online event
  const handleOnline = useCallback(() => {
    setIsOnline(true);
    setLastOnlineAt(new Date());
    updateConnectionInfo();

    // Trigger a refresh of offline status
    refreshStatus();

    // Dispatch custom event for other components
    window.dispatchEvent(new CustomEvent('app:online'));
  }, [updateConnectionInfo, refreshStatus]);

  // Handle offline event
  const handleOffline = useCallback(() => {
    setIsOnline(false);
    updateConnectionInfo();

    // Dispatch custom event for other components
    window.dispatchEvent(new CustomEvent('app:offline'));
  }, [updateConnectionInfo]);

  // Handle connection change
  const handleConnectionChange = useCallback(() => {
    updateConnectionInfo();
  }, [updateConnectionInfo]);

  useEffect(() => {
    // Set initial state
    setIsOnline(navigator.onLine);
    if (navigator.onLine) {
      setLastOnlineAt(new Date());
    }
    updateConnectionInfo();
    refreshStatus();

    // Add event listeners
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    // Network Information API (if available)
    const connection =
      navigator.connection ||
      navigator.mozConnection ||
      navigator.webkitConnection;

    if (connection) {
      connection.addEventListener('change', handleConnectionChange);
    }

    // Periodically check connection and sync queue
    const intervalId = setInterval(() => {
      // Check if navigator.onLine matches our state
      if (navigator.onLine !== isOnline) {
        if (navigator.onLine) {
          handleOnline();
        } else {
          handleOffline();
        }
      }

      // Refresh sync queue count
      refreshStatus();
    }, 30000); // Every 30 seconds

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);

      if (connection) {
        connection.removeEventListener('change', handleConnectionChange);
      }

      clearInterval(intervalId);
    };
  }, [handleOnline, handleOffline, handleConnectionChange, updateConnectionInfo, refreshStatus, isOnline]);

  return {
    isOnline,
    isOffline: !isOnline,
    lastOnlineAt,
    lastSyncAt,
    syncQueueCount,
    connectionType: connectionInfo.type,
    effectiveType: connectionInfo.effectiveType,
    downlink: connectionInfo.downlink,
    rtt: connectionInfo.rtt,
    refreshStatus,
  };
}

// Simpler hook that just returns online/offline boolean
export function useIsOnline(): boolean {
  const [isOnline, setIsOnline] = useState<boolean>(
    typeof navigator !== 'undefined' ? navigator.onLine : true
  );

  useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  return isOnline;
}

export default useOfflineStatus;
